/**
 * WorkOrderLinkButton - Renders a card on a report with a button that opens the
 * organization's external work order system, when configured.
 *
 * Phase 1 of the work order integration: opens the configured URL in the
 * browser / system handler. Renders nothing when the org has no work order
 * link enabled (or while loading), so it's safe to drop into any report view.
 */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Linking } from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import { getWorkOrderConfig, normalizeWorkOrderUrl } from '../util/workOrderConfig';
import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import AppButton from './AppButton';
import { useTranslation } from '../i18n';

/**
 * @param {string} organization - Organization the report belongs to
 */
const WorkOrderLinkButton = ({ organization }) => {
  const { t } = useTranslation('reports');

  const [config, setConfig] = useState(null);

  useEffect(() => {
    let active = true;
    if (!organization) {
      setConfig(null);
      return;
    }
    getWorkOrderConfig(organization).then((cfg) => {
      if (active) setConfig(cfg);
    });
    return () => {
      active = false;
    };
  }, [organization]);

  const url = normalizeWorkOrderUrl(config?.url);
  if (!config?.enabled || !url) return null;

  const handlePress = () => {
    Linking.openURL(url).catch(() => {});
  };

  return (
    <View style={styles.card}>
      <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
        {t('workOrderButton.label')}
      </CustomText>
      <CustomText
        style={styles.subtitle}
        font="regular"
        size={14}
        color={AppStyles.colorSet.textSecondary}
      >
        {t('workOrderButton.hint')}
      </CustomText>
      <View style={styles.buttonWrap}>
        <AppButton
          title={config.label}
          onPress={handlePress}
          variant="primary"
          icon="external-link"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 20,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginVertical: hp(1),
  },
  subtitle: {
    marginTop: 4,
    lineHeight: 20,
  },
  buttonWrap: {
    alignItems: 'center',
    marginTop: hp(1.5),
  },
});

export default WorkOrderLinkButton;
